/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Top-down creation of ONE cp.ProvisionContract together with 1..N cp.ProvisionContractAgent
 *         rows in a single run. Hosts two mvs-crud-object forms in capture-only mode and owns the
 *         write sequence itself, so the Provisionszeile and its Provisionssätze are written in one
 *         controlled, resumable order and nothing is written before the user confirms.
 */
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Observable, of, Subscription, throwError} from 'rxjs';
import {switchMap} from 'rxjs/operators';

import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {MvsFormControlOverwrite} from 'features/core/shared/logic/form/logic/mvs-form-control-overwrite';
import {MvsFormFieldAccessEnum} from 'features/core/shared/logic/form/logic/mvs-form-field-access.enum';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';

import {ProvisionContractDto} from '../../../model/dto/entity/provision-contract.dto';
import {ProvisionContractAgentDto} from '../../../model/dto/entity/provision-contract-agent.dto';
import {CpFeatureRight} from '../../../service/domain/cp-feature-right';
import {
    CpWizardFieldState,
    CpProvisionContractAgentDraft,
    CpProvisionContractWizardStateService
} from '../../../service/domain/cp-provision-contract-wizard-state.service';

/** The three steps of the create wizard. */
type CpProvisionContractCreateStep = 'contract' | 'agents' | 'summary';

/**
 * Anlage-Wizard: eine Provisionszeile (`cp.ProvisionContract`) und dazu 1..N Provisionssätze
 * (`cp.ProvisionContractAgent`) in EINEM Durchlauf, top-down.
 *
 * <h2>Two modes</h2>
 * <ul>
 *   <li><b>New Provisionszeile</b> (default) — step 1 captures the parent, the save sequence writes
 *       it and then its Provisionssätze.</li>
 *   <li><b>Existing Provisionszeile</b> ({@link provisionContractId} set) — step 1 becomes a
 *       read-only context card of the row that was handed in, nothing is written on the parent, and
 *       the run only adds Provisionssätze to it. This is the shape the action "Satz manuell anlegen"
 *       needs: creating a second time slice there would change what the action means and would run
 *       into the uniqueness rule of the parent.</li>
 * </ul>
 * The two share one write sequence rather than forking: the existing row is adopted onto the same
 * field a written parent would occupy, so {@code ensureContractCreated} finds a parent and writes
 * nothing — the identical early return that makes a retry reuse a parent from a failed attempt.
 *
 * <h2>Mechanics — the component owns the save, not the form</h2>
 * Both embedded {@code <mvs-crud-object>} instances run with {@code hideDefaultActions = true}, i.e.
 * CAPTURE ONLY: they render the entity's own metadata form and report values through
 * {@code (onFormChange)} and submittability through {@code (onFormSubmitStateChange)}, but they
 * never write anything themselves. The write order, the retry behaviour and the FK chaining belong
 * to this component and to {@link CpProvisionContractWizardStateService}.
 *
 * That is the LATER of the two wizard patterns in this repo and it is the binding one here, because
 * it is the only one that satisfies the four invariants below. The older pattern — the address and
 * bank wizards — lets the FORM own the save, takes the parent key off {@code event.after.id}, has no
 * double-submit lock and no re-entry handling, and its first step writes immediately. Its OPTIC is
 * reused here (stacked bordered step cards, numbered circle, status badge); its mechanics are not.
 *
 * <h2>The four invariants, and where each one lives</h2>
 * <ol>
 *   <li><b>No orphan BEFORE the save sequence — and a deliberate one after a partial failure.</b>
 *       Nothing is written until {@link handleSave} runs; every step captures into a draft, so
 *       abandoning the dialog during capture leaves no Provisionszeile behind. That matters, because
 *       the parent occupies the unique time slice (Vertrag, Provisionsart, Ereignisdatum) and an
 *       orphan would block the very run that meant to create it.
 *       <p>AFTER the sequence has started, that guarantee no longer holds, and it must not be read
 *       as if it did. If the parent was written and a child then failed, cancelling KEEPS the
 *       parent — on purpose: it is the resume point that lets "Erneut versuchen" finish the run
 *       instead of colliding with its own half-written result. Deleting it is not the answer
 *       (it may already carry children, and in existing-parent mode the row is not even ours), so
 *       the wizard does the other thing that honesty allows: it SAYS so before the user cancels,
 *       says it again as they do, and announces the row so the surrounding list shows it instead of
 *       hiding it. See {@link handleCancel}.</li>
 *   <li><b>No double submit.</b> {@link CpProvisionContractWizardStateService#beginSaveSequence}
 *       raises the single-flight lock BEFORE the first request leaves and refuses a second start.
 *       Cancel is refused while a write is in flight.</li>
 *   <li><b>Resumable retry.</b> Every write is guarded by its own resume point, so "Erneut
 *       versuchen" continues at the failed write and never repeats a successful one. Without it, a
 *       failure on the second Provisionssatz would make the retry try to create the Provisionszeile
 *       a second time — and be refused by its own uniqueness rule, leaving the run permanently
 *       stuck.</li>
 *   <li><b>The time-slice refusal is SHOWN.</b> The rule lives in
 *       {@code ProvisionContractOnChange.assertUniqueEventKey} and stays there. This wizard runs no
 *       pre-flight query and re-implements nothing; it renders the server's own German refusal and
 *       adds one sentence saying what to change.</li>
 * </ol>
 *
 * <h2>What the wizard decides for the user, and what it refuses to invent</h2>
 * The Provisionssatz form never shows the Provisionszeile: at capture time the parent does not
 * exist. The three denormalised keys are not shown either — the backend REFUSES a row whose copies
 * disagree with the parent, so they are derived from the parent response in
 * {@link CpProvisionContractWizardStateService#buildAgentPayload}. {@code source} is left alone;
 * {@code ProvisionContractAgentOnChange.forceManualSource} stamps MANUAL on every row that reaches
 * the generic stack.
 *
 * Everything else the entity metadata declares mandatory is ASKED, not guessed. Inventing a
 * liability start or a base amount here would create a row the engine then disagrees with — the
 * same reason the existing "Satz manuell anlegen" dialog gives for deriving nothing.
 *
 * <h2>Nothing is calculated on creation (signed design), and the closing step splits that honestly</h2>
 * A manually created row does not run through the calculation pipeline. The closing step therefore
 * names every field the caseworker would otherwise look for — but in TWO groups, because the two are
 * not the same promise:
 * <ul>
 *   <li><b>Deferred</b> — four nullable fields of the Provisionszeile that are never asked and that
 *       the next ProvisionRun fills in. They carry the pending sentence
 *       ({@link getDeferredContractFields}).</li>
 *   <li><b>Captured</b> — {@code eligibility} on the Provisionszeile and the five liability fields on
 *       the Provisionssatz. Their columns are NOT NULL without a default, so the row cannot exist
 *       without them: the wizard ASKS for them and shows the value the user gave
 *       ({@link getCapturedContractFields} / {@link getCapturedAgentFields}). The pending sentence
 *       must never appear over one of these — it would describe the user's own input as
 *       provisional.</li>
 * </ul>
 * Both groups render a value or a sentence, never an empty cell.
 *
 * <p>This wizard is ADDITIVE. The three existing action dialogs of the module (Betrag überschreiben,
 * Berater wechseln, Satz manuell anlegen) are untouched and keep running next to it.
 *
 * German UI text with real umlauts, English code and comments (module convention).
 */
@Component({
    selector: 'cp-provision-contract-create-wizard',
    templateUrl: './cp-provision-contract-create-wizard.component.html',
    styleUrls: ['./cp-provision-contract-create-wizard.component.scss'],
    providers: [CpProvisionContractWizardStateService],
    standalone: false
})
export class CpProvisionContractCreateWizardComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * Fields of the Provisionszeile the ENGINE owns.
     *
     * Left out of the capture form with {@link MvsFormFieldAccessEnum#EXCLUDED} — no control at all,
     * so the key never appears in the payload. HIDDEN would be the wrong choice: it still submits
     * the form's snapshot, which for these fields is a null the backend merge reads as "clear this
     * attribute".
     *
     * {@code applicationDate} is NOT in this list although the entity form marks it read-only — see
     * {@link buildContractFormControlOverwrite}.
     */
    protected static readonly CONTRACT_ENGINE_FIELDS: string[] = [
        'provisionBaseDtoId',
        'appliedBaseCalculationMode',
        'baseReferenceAmountMonthly',
        'baseAmountMonthly',
        'baseAmountTotal',
        'agentsResolved',
        'endDate',

        // NOT NULL, read-only and server-derived — all three at once, which is exactly the
        // combination that deadlocks a form. Leaving it with a control gave it a `required`
        // validator on a field the UI renders disabled and empty: the form stays INVALID forever and
        // "Weiter" can never be enabled. EXCLUDED removes the control, so there is no validator to
        // satisfy. See buildContractFormControlOverwrite for why it is not offered as an input.
        'applicationDate',

        'triggeringContractPriceId',
        'cancellationDate',
        'cancellationReason'
    ];

    /**
     * Fields of the Provisionssatz the ENGINE owns or the WIZARD decides.
     *
     * The first four are decided by the wizard (parent FK plus the three denormalised keys, all
     * derived from the created parent); {@code source} is stamped by the backend; the rest are run
     * results that no caseworker can state at creation time.
     */
    /** Entity alias of the Abrechnungskonto — the row a Provisionssatz is booked onto. */
    protected static readonly ALIAS_PROVISION_AGENT: string = 'cp.ProvisionAgent';

    /** Entity alias of the Provisionsart. */
    protected static readonly ALIAS_PROVISION_TYPE_COMPONENT: string = 'cp.ProvisionTypeComponent';

    /** Entity alias of the cm.Contract the Provisionszeile hangs on. */
    protected static readonly ALIAS_CONTRACT: string = 'cm.Contract';

    /** Entity alias of the Provisionstyp. */
    protected static readonly ALIAS_PROVISION_TYPE: string = 'cp.ProvisionType';

    protected static readonly AGENT_DERIVED_FIELDS: string[] = [
        'provisionContractDtoId',
        'contractDtoId',
        'provisionTypeDtoId',
        'provisionTypeContractEventDtoId',
        'source',
        'agentManuallyChanged',
        'cancellationDate',
        'cancellationLiabilityAmount',

        // The ten fields the run owns outright — see DEFERRED_CONTRACT_AGENT_FIELDS. A field whose
        // only writer is the run must not be offered as an input: whatever the user typed would be
        // replaced by the next run without a word, which is worse than never having asked.
        'liabilityTypeAssignmentDtoId',
        'liabilityTypeDtoId',
        'appliedLiabilitySource',
        'provisionTypeMatrixDtoId',
        'appliedCalcMode',
        'appliedFactor',
        'appliedReservePercentage',
        'provisionAmount',
        'reserveTargetAmount',
        'provisionTargetAmount'
    ];

    initialized: boolean = false;

    /** Visibility of the dialog; intended as a two-way binding ({@code [(visible)]}). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /**
     * um right {@code cp_agentChange} — optimistic default, demoted on a server 403.
     *
     * Both writes of this wizard run under it: the parent create is the same logical act as the
     * child create, and the child create is guarded by it on the backend.
     */
    @Input() canAgentChange: boolean = true;

    /** Optional context: the Abrechnungskonto the wizard was opened from; shown, never written. */
    @Input() provisionAgentId: number;

    /** Display name of the Abrechnungskonto; purely informational. */
    @Input() provisionAgentName: string;

    /**
     * An EXISTING Provisionszeile to hang the new Provisionssätze on.
     *
     * Set: the wizard adds rows to that row. Step 1 stops being a capture step and becomes a
     * read-only context card, the Provisionszeile is never written, and the three denormalised keys
     * are derived from the loaded parent. This is the shape the action "Satz manuell anlegen" needs:
     * there the Provisionszeile already exists and the user only wants a Provisionssatz on it —
     * creating a second time slice would be a different act with a different meaning, and would run
     * straight into the uniqueness rule.
     *
     * Not set: unchanged behaviour — the wizard creates the Provisionszeile itself in step 1.
     */
    @Input() provisionContractId: number;

    /** Optional prefill of the Provisionszeile — e.g. the cm.Contract of the calling surface. */
    @Input() contractId: number;

    /** Optional ambient context handed to both embedded forms; scopes the backend value lists. */
    @Input() importObjectContext: DtoImportObjectContext;

    /** Fires after the whole run succeeded, with the created Provisionszeile. */
    @Output() onContractCreated = new EventEmitter<ProvisionContractDto>();

    /** Fires when the server refused the right with a 403 — the demotion signal. */
    @Output() onForbidden = new EventEmitter<void>();

    /** The user abandoned the run. Nothing was created unless a save had already failed midway. */
    @Output() onCancel = new EventEmitter<void>();

    /** Preset DTO of the Provisionszeile form; a stable reference, rebuilt only on (re-)open. */
    contractDefaultDto: DtoDetail;

    /** Field access of the Provisionszeile form; a stable reference, built once per run. */
    contractFormControlOverwrite: MvsFormControlOverwrite;

    /** Preset DTO of the Provisionssatz form; replaced after every captured draft to reset it. */
    agentDefaultDto: DtoDetail;

    /** Field access of the Provisionssatz form; a stable reference, built once per run. */
    agentFormControlOverwrite: MvsFormControlOverwrite;

    /** Context of both embedded forms, incl. the Abrechnungskonto entry when one is known. */
    formImportObjectContext: DtoImportObjectContext;


    /** The block-head explanation, from the ONE place both wizards and the detail view share. */
    readonly deferredExplanation: string = CpProvisionContractWizardStateService.DEFERRED_EXPLANATION;

    /** True once the whole run succeeded — the dialog then shows its result panel. */
    completed: boolean = false;

    /**
     * Display labels of the Provisionszeile references, resolved when the capture step is left.
     *
     * Not read from the form value: `<field>DtoName` there is a snapshot from form generation and
     * does not follow the user's selection. Not read from the field's option list either — a
     * reference field carries none (see CpProvisionContractWizardStateService.resolveReferenceLabel
     * for the measurement). Resolved once per decision instead.
     */
    contractLabel: string = null;
    provisionTypeLabel: string = null;

    /** Loading the existing Provisionszeile of the existing-parent mode. */
    contractLoadBusy: boolean = false;

    /** German message of a failed parent load; null while nothing failed. */
    contractLoadError: string = null;

    protected subscription: Subscription = new Subscription();

    /** German date rendering for the summary — the module's own format, not a hand-rolled one. */
    protected datePipe: DatePipe = new DatePipe('de-DE');

    constructor(
        public state: CpProvisionContractWizardStateService,
        protected messageService: MvsMessageService,
        protected mvsObjectService: MvsObjectService) {
    }

    ngOnInit(): void {

        this.initialized = true;

        if (this.visible) {
            this.refreshComponent();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        // A changed parent id is a different run, not the same one continued - it decides the mode,
        // the context card and the keys every child is derived from.
        if (this.visible && (changes['visible'] || changes['provisionContractId'])) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Return the whole run to its starting state.
     *
     * Rebuilt fresh on every open: values, drafts and — deliberately — the resume points. A new run
     * targets a different time slice, so inheriting the parent of the previous run would attach new
     * Provisionssätze to the wrong Provisionszeile.
     */
    refreshComponent(): void {

        this.state.reset();
        this.state.steps = ['contract', 'agents', 'summary'];

        this.completed = false;

        this.formImportObjectContext = this.buildImportObjectContext();
        this.state.importObjectContext = this.formImportObjectContext;

        this.contractFormControlOverwrite = this.buildContractFormControlOverwrite();
        this.agentFormControlOverwrite = this.buildAgentFormControlOverwrite();

        this.contractDefaultDto = this.buildContractDefaultDto();
        this.agentDefaultDto = this.buildAgentDefaultDto();

        this.contractLoadError = null;
        this.contractLoadBusy = false;

        this.contractLabel = null;
        this.provisionTypeLabel = null;

        if (this.provisionContractId) {
            this.loadExistingContract();
        }
    }

    // ---------------------------------------------------------------------------------------- //
    // Existing-parent mode
    // ---------------------------------------------------------------------------------------- //

    /** Whether this run adds Provisionssätze to a Provisionszeile that already exists. */
    hasExistingContract(): boolean {
        return !!this.provisionContractId;
    }

    /** The adopted parent — the loaded row in existing-parent mode, else the row this run created. */
    getExistingContract(): ProvisionContractDto {
        return this.state.createdContract;
    }

    /**
     * Fetch the Provisionszeile the caller named and adopt it as the parent of this run.
     *
     * Whether the load succeeded decides whether the run may start at all: without the parent the
     * wizard has no Vertrag, no Provisionsart and no Ereignis to copy onto its children, and every
     * child write would be refused for a rule the user did not break.
     */
    protected loadExistingContract(): void {

        this.contractLoadBusy = true;
        this.contractLoadError = null;

        this.subscription.add(
            this.state.loadExistingContract(this.provisionContractId).subscribe({
                next: () => {
                    this.contractLoadBusy = false;

                    if (!this.state.hasDerivableKeys()) {
                        this.contractLoadError =
                            'Die Provisionszeile konnte geladen werden, führt aber weder Vertrag noch '
                            + 'Provisionsart und Ereignis mit. Ohne diese drei Angaben lässt sich kein '
                            + 'Provisionssatz zu ihr anlegen.';
                    }
                },
                error: () => {
                    this.contractLoadBusy = false;
                    this.contractLoadError =
                        'Die Provisionszeile konnte nicht geladen werden. Bitte erneut versuchen.';
                }
            })
        );
    }

    handleRetryContractLoad(): void {

        if (this.contractLoadBusy) {
            return;
        }

        this.loadExistingContract();
    }

    /** Whether the run is blocked because its parent is missing or unusable. */
    isExistingContractUnusable(): boolean {
        return this.hasExistingContract() && (!!this.contractLoadError || !this.state.createdContract);
    }

    // ---------------------------------------------------------------------------------------- //
    // Form configuration
    // ---------------------------------------------------------------------------------------- //

    /**
     * Ambient context of both embedded forms.
     *
     * The caller's context is COPIED, so the caller's own instance — which may drive other forms of
     * the same surface — is never mutated.
     *
     * <p><b>The Abrechnungskonto is deliberately NOT added.</b> An earlier version put a
     * {@code cp.ProvisionAgent} entry here to scope the backend value lists. Measured effect: the
     * generic stack treats a foreign key SUPPLIED BY THE CONTEXT as already decided and renders it
     * {@code hidden} — the Berater field disappeared from the form altogether, and every row of a run
     * was booked onto the account the wizard happened to be opened from. That is the opposite of what
     * this step is for.
     *
     * <p>The distinction that matters, and the reason the fix is not "pass a different context":
     * {@code importObjectContext} FIXES a value and hides the field; {@code defaultCreateDto}
     * PRESETS one and leaves it editable. The Berater wants the second, so it is prefilled in
     * {@link buildAgentDefaultDto} instead — the user opens the step on the account they came from
     * and can pick another.
     */
    protected buildImportObjectContext(): DtoImportObjectContext {

        return this.importObjectContext
            ? this.importObjectContext.copy()
            : DtoImportObjectContext.createEmpty();
    }

    /**
     * Prefill of the Provisionssatz form — the Abrechnungskonto the wizard was opened from.
     *
     * A PRESET, not a constraint: the field renders as a normal picker carrying this value, and the
     * user may choose a different Berater. That is the whole point of the step — the case the manual
     * creation exists for is precisely the one where the automatic agent assignment was wrong.
     *
     * <p>A NEW instance on every call: the embedded form re-renders on an input identity change,
     * which is what clears the controls after a draft has been taken over.
     */
    protected buildAgentDefaultDto(): DtoDetail {

        const dto = new DtoDetail();

        if (this.provisionAgentId) {
            dto['provisionAgentDtoId'] = this.provisionAgentId;
        }

        return dto;
    }

    /**
     * Prefill of the Provisionszeile form.
     *
     * {@code eligibility} is preset to ELIGIBLE, and that is not a convenience. A hand-made row only
     * reaches the calculation if the selection catches it, and the branch written for exactly this
     * case wants {@code agents_resolved = FALSE AND eligibility = ELIGIBLE}
     * ({@code ProvisionContractSelectionService:133-139}). The default is therefore the one value
     * that makes the row behave the way someone creating it expects. Anything else stays available —
     * it just gets a warning, see {@link isOutsideRunSelection}.
     */
    protected buildContractDefaultDto(): DtoDetail {

        const dto = new DtoDetail();

        if (this.contractId) {
            dto['contractDtoId'] = this.contractId;
        }

        dto['eligibility'] = CpProvisionContractWizardStateService.ELIGIBILITY_ELIGIBLE;

        return dto;
    }
    /**
     * Would the run pass this Provisionszeile by, given what is captured?
     *
     * Delegates to the state container, which holds the rule and its evidence. Rendered as a warning
     * next to the field and again in the closing step — never as a block, because the choice is the
     * user's to make.
     */
    isOutsideRunSelection(): boolean {
        return this.state.isOutsideRunSelection(this.state.contractDraft);
    }

    /** The German warning, in ONE place so the two render sites cannot drift apart. */
    getSelectionWarning(): string {

        return 'Mit einer anderen Relevanz als „Provisionsrelevant" wird diese Provisionszeile vom '
            + 'Berechnungslauf möglicherweise nicht aufgegriffen — dann bleiben die oben genannten '
            + 'Felder dauerhaft leer. Setzen Sie alternativ eine Relevanz-Überschreibung, die den Lauf '
            + 'ebenfalls auf die Zeile führt.';
    }


    /**
     * Field access of the Provisionszeile form.
     *
     * Two decisions, both deliberate:
     *
     * <p><b>EXCLUDED for the engine-owned fields.</b> They are filled by the next ProvisionRun, so
     * offering them as inputs would invite a caseworker to state a base amount the engine then
     * disagrees with. They are not silently dropped either — the closing step names every one of
     * them and says what will happen to it.
     *
     * <p><b>{@code applicationDate} is NOT raised here, and must not be.</b> An earlier version of
     * this method lifted it to UPDATE, reasoning that a row which does not exist yet has no key date
     * to move. The reasoning was wrong about where the block lives: the field carries
     * {@code uiReadOnly} on the ENTITY ({@code ProvisionContract.java:304}), and read-only is
     * re-derived server-side on every write from {@code EntityMetaAttribute.isReadOnly()}, so
     * {@code DtoService.mapDtoToAccessObject} discards the value unconditionally — before any
     * create/update distinction. An overwrite changes only how the CLIENT renders, which made the
     * input a phantom: the user typed a date that never arrived.
     *
     * <p>The column is filled where it belongs instead — {@code ProvisionContractAccess}
     * derives it on create from the linked {@code cm.Contract} (its application date, else its start
     * date). Do not re-add the overwrite: it cannot work, and it lies to the user while it fails.
     */
    protected buildContractFormControlOverwrite(): MvsFormControlOverwrite {

        const overwrite = new MvsFormControlOverwrite();

        for (const field of CpProvisionContractCreateWizardComponent.CONTRACT_ENGINE_FIELDS) {
            overwrite.addField(field, MvsFormFieldAccessEnum.EXCLUDED);
        }

        return overwrite;
    }

    /**
     * Field access of the Provisionssatz form.
     *
     * <p><b>EXCLUDED for the derived and engine-owned fields</b> — see
     * {@link #AGENT_DERIVED_FIELDS}. The parent FK and the three denormalised keys are added to the
     * payload from the parent response; they must not be offered, because at capture time the parent
     * does not exist yet and a value the user typed could only disagree with it.
     *
     * <p><b>{@code provisionAgentDtoId} needs no entry here, and must not get one.</b> The Berater is
     * selectable on a CREATE because the backend form says so: {@code ProvisionContractAgentForm}
     * scopes its read-only lock to {@code objectAccess.isEntityPersisted()}, so the lock applies to
     * an existing Provisionssatz and not to one being made. Choosing the Berater is the entire point
     * of this step, and the form now renders it that way on its own.
     *
     * <p>WHY THE LOCK EXISTS AT ALL, so the split does not read as an inconsistency: changing the
     * agent of an EXISTING row is a book-keeping operation, not a field edit — the bookings of the
     * previous agent have to be reversed and re-booked, which is what
     * {@code POST /{id}/changeAgent} does, and it additionally stamps {@code agentManuallyChanged} so
     * a later run does not pull the agent back. On a row being created there is no predecessor, no
     * booking to move, and no flag to stamp: the equivalent guarantee comes from
     * {@code source = MANUAL}, which the server forces on every row that reaches the generic stack.
     * The ADJUST wizard therefore keeps the field out of its form entirely and points at the existing
     * "Berater wechseln" dialog.
     *
     * <p>An earlier version raised the field with an overwrite. That never worked and could not: the
     * lock came from the FORM, and an overwrite only changes how the client renders a field the form
     * did not offer in the first place. It was removed once the form itself was fixed — do not
     * re-add it. (Note the difference to {@code applicationDate}: there the value was actively
     * DISCARDED server-side; here it was only unselectable, and a value supplied from the context
     * always arrived correctly.)
     */
    protected buildAgentFormControlOverwrite(): MvsFormControlOverwrite {

        const overwrite = new MvsFormControlOverwrite();

        for (const field of CpProvisionContractCreateWizardComponent.AGENT_DERIVED_FIELDS) {
            overwrite.addField(field, MvsFormFieldAccessEnum.EXCLUDED);
        }

        return overwrite;
    }

    // ---------------------------------------------------------------------------------------- //
    // Step 1 — Provisionszeile
    // ---------------------------------------------------------------------------------------- //

    handleContractFormChange(value: DtoDetail): void {
        this.state.contractDraft = value;
    }

    handleContractSubmitStateChange(submittable: boolean): void {
        this.state.contractSubmittable = submittable;
    }

    /**
     * German summary of the Provisionszeile, for the collapsed step card and the closing step.
     *
     * The two references are NOT read off `<field>DtoName` for a captured draft: that name is a
     * snapshot from form generation and keeps naming the prefilled row after the user picked another.
     * They come from {@link resolveContractLabels}, which resolves them once when the step is left.
     * For an EXISTING parent the row came from the server, so its names describe that very row and
     * are authoritative.
     */
    getContractSummary(): string {

        const draft = this.hasExistingContract() ? this.getExistingContract() : this.state.contractDraft;

        if (!draft) {
            return this.hasExistingContract() ? 'Wird geladen …' : 'Noch nicht erfasst';
        }

        const parts: string[] = [];
        const existing = this.hasExistingContract();

        const contractName = existing ? draft['contractDtoName'] : this.contractLabel;
        const provisionTypeName = existing ? draft['provisionTypeDtoName'] : this.provisionTypeLabel;

        if (contractName) {
            parts.push(String(contractName));
        }

        if (provisionTypeName) {
            parts.push(String(provisionTypeName));
        }

        // Through the DatePipe rather than String(): a raw Date stringifies to
        // "Tue Sep 15 2026 02:00:00 GMT+0200 (…)", which a caseworker reads as a defect.
        const startDate = this.formatDate(draft['startDate']);

        if (startDate) {
            parts.push('ab ' + startDate);
        }

        return parts.length ? parts.join(' · ') : 'Erfasst';
    }

    // ---------------------------------------------------------------------------------------- //
    // Step 2 — Provisionssätze
    // ---------------------------------------------------------------------------------------- //

    handleAgentFormChange(value: DtoDetail): void {
        this.state.agentDraft = value;
    }

    handleAgentSubmitStateChange(submittable: boolean): void {
        this.state.agentSubmittable = submittable;
    }

    canAddAgentDraft(): boolean {
        return this.state.agentSubmittable && !!this.state.agentDraft && !this.state.saveBusy;
    }

    /**
     * Take the captured Provisionssatz over into the list and clear the form for the next one.
     *
     * NOTHING is written here. The form is cleared by handing it a NEW default DTO instance: the
     * embedded component re-renders on an input identity change, which is what resets the controls.
     */
    handleAddAgentDraft(): void {

        if (!this.canAddAgentDraft()) {
            return;
        }

        const draft = this.state.addAgentDraft(this.state.agentDraft);

        this.resolveAgentDraftLabels(draft);

        this.agentDefaultDto = this.buildAgentDefaultDto();
    }

    /**
     * Fetch the display names of the two references the draft points at.
     *
     * Fired by an explicit user action, exactly once per draft. A failure is deliberately silent: the
     * label stays null and the summary shows its neutral fallback — a display lookup must never break
     * the capture flow, and nothing about the draft's DATA depends on it.
     */
    protected resolveAgentDraftLabels(draft: CpProvisionContractAgentDraft): void {

        const value = draft?.value;

        if (!value) {
            return;
        }

        this.subscription.add(
            this.state.resolveReferenceLabel(
                CpProvisionContractCreateWizardComponent.ALIAS_PROVISION_AGENT,
                value['provisionAgentDtoId']).subscribe({
                next: (label: string) => draft.agentLabel = label,
                error: () => draft.agentLabel = null
            }));

        this.subscription.add(
            this.state.resolveReferenceLabel(
                CpProvisionContractCreateWizardComponent.ALIAS_PROVISION_TYPE_COMPONENT,
                value['provisionTypeComponentDtoId']).subscribe({
                next: (label: string) => draft.componentLabel = label,
                error: () => draft.componentLabel = null
            }));
    }

    handleRemoveAgentDraft(key: number): void {

        if (this.state.saveBusy) {
            return;
        }

        this.state.removeAgentDraft(key);
    }

    /** German summary of one captured draft, for the list row. */
    getAgentDraftSummary(draft: CpProvisionContractAgentDraft): string {

        const value = draft?.value;

        if (!value) {
            return 'Provisionssatz';
        }

        const parts: string[] = [];

        // Resolved once when the draft was taken over and stored on it — never looked up here, where
        // change detection would turn every render into a request.
        const component = draft.componentLabel;
        const agent = draft.agentLabel;

        if (component) {
            parts.push(component);
        }

        if (agent) {
            parts.push(agent);
        }

        return parts.length ? parts.join(' · ') : 'Provisionssatz';
    }

    // ---------------------------------------------------------------------------------------- //
    // Navigation
    // ---------------------------------------------------------------------------------------- //

    isStepActive(step: CpProvisionContractCreateStep): boolean {
        return this.state.isStepActive(step);
    }

    isStepDecided(step: CpProvisionContractCreateStep): boolean {
        return this.state.isStepDecided(step);
    }

    getStepNumber(step: CpProvisionContractCreateStep): number {
        return this.state.getStepNumber(step);
    }

    canGoBack(): boolean {
        return this.state.activeStepIndex > 0 && !this.state.saveBusy && !this.state.isLocked();
    }

    canGoForward(): boolean {

        if (this.state.saveBusy || this.state.isLastStep()) {
            return false;
        }

        if (this.isStepActive('contract')) {

            // Nothing is captured here in existing-parent mode - the step only shows what the new
            // Provisionssätze will hang on. It may be left as soon as that row is actually there.
            if (this.hasExistingContract()) {
                return !this.contractLoadBusy && !this.isExistingContractUnusable();
            }

            return this.state.contractSubmittable && !!this.state.contractDraft;
        }

        if (this.isStepActive('agents')) {
            return this.state.agentDrafts.length > 0;
        }

        return false;
    }

    handleBack(): void {

        if (!this.canGoBack()) {
            return;
        }

        this.state.goToStep(this.state.activeStepIndex - 1);
    }

    handleForward(): void {

        if (!this.canGoForward()) {
            return;
        }

        // Leaving the capture step is a decision — the moment to resolve its labels ONCE, rather
        // than on every render of the summary that follows.
        if (this.isStepActive('contract') && !this.hasExistingContract()) {
            this.resolveContractLabels();
        }

        this.state.goToStep(this.state.activeStepIndex + 1);
    }

    /** Fetch the display names of the two Provisionszeile references; silent on failure. */
    protected resolveContractLabels(): void {

        const draft = this.state.contractDraft;

        if (!draft) {
            return;
        }

        this.subscription.add(
            this.state.resolveReferenceLabel(
                CpProvisionContractCreateWizardComponent.ALIAS_CONTRACT,
                draft['contractDtoId']).subscribe({
                next: (label: string) => this.contractLabel = label,
                error: () => this.contractLabel = null
            }));

        this.subscription.add(
            this.state.resolveReferenceLabel(
                CpProvisionContractCreateWizardComponent.ALIAS_PROVISION_TYPE,
                draft['provisionTypeDtoId']).subscribe({
                next: (label: string) => this.provisionTypeLabel = label,
                error: () => this.provisionTypeLabel = null
            }));
    }

    /**
     * A date as the module writes it — {@code dd.MM.yyyy}, through Angular's own DatePipe.
     *
     * The value arrives as whatever the form control holds (a Date, or an ISO string), so it is
     * handed to the pipe rather than concatenated. A value the pipe cannot read yields null and the
     * caller omits the part instead of printing something unreadable.
     */
    protected formatDate(value: any): string {

        if (!value) {
            return null;
        }

        try {
            return this.datePipe.transform(value, 'dd.MM.yyyy');
        } catch {
            return null;
        }
    }

    /**
     * "Ändern" on a collapsed step — only while nothing has been written.
     *
     * Never offered for the Provisionszeile in existing-parent mode: that row is context, not a
     * decision of this run, and this wizard has no path that would edit it. Changing it is the
     * separate job of the adjust wizard.
     */
    canChangeStep(step: CpProvisionContractCreateStep): boolean {

        if (step === 'contract' && this.hasExistingContract()) {
            return false;
        }

        return this.isStepDecided(step) && !this.state.isLocked() && !this.state.saveBusy;
    }

    handleChangeStep(step: CpProvisionContractCreateStep): void {

        if (!this.canChangeStep(step)) {
            return;
        }

        this.state.goToStep(this.state.steps.indexOf(step));
    }

    // ---------------------------------------------------------------------------------------- //
    // Closing step — the honesty duty, in its two honest halves
    // ---------------------------------------------------------------------------------------- //

    /**
     * The five deferred fields of the Provisionszeile.
     *
     * WHICH SOURCE is the entire point here, and it differs by mode:
     *
     * <ul>
     *   <li><b>New parent</b> — the capture draft. None of the five is in the form, so all five
     *       render the pending sentence, which is exactly right: the row does not exist yet.</li>
     *   <li><b>Existing parent</b> — the LOADED row, which may have been through a run already. A
     *       field that carries a figure shows the figure, labelled "aus der bestehenden Zeile"; only
     *       the ones still empty keep the pending sentence. Reading the (empty) draft here instead
     *       would tell the user the next run still owes five values that are long since computed —
     *       the mirror image of the empty-field problem this panel exists to prevent.</li>
     * </ul>
     */
    getDeferredContractFields(): CpWizardFieldState[] {

        if (this.hasExistingContract()) {
            return this.state.describeDeferredContractFields(
                this.getExistingContract(), CpProvisionContractWizardStateService.EXISTING_HINT);
        }

        return this.state.describeDeferredContractFields(this.state.contractDraft);
    }

    /**
     * The Provisionszeile field the user states.
     *
     * Same source rule as above. In existing-parent mode the value was stated by whoever created that
     * row, so it is labelled "aus der bestehenden Zeile" rather than "von Ihnen erfasst" — crediting
     * the current user with an entry they never made would be a small lie in the same panel that
     * exists to stop lies.
     */
    getCapturedContractFields(): CpWizardFieldState[] {

        if (this.hasExistingContract()) {
            return this.state.describeCapturedContractFields(
                this.getExistingContract(), CpProvisionContractWizardStateService.EXISTING_HINT);
        }

        return this.state.describeCapturedContractFields(this.state.contractDraft);
    }

    /**
     * The ten deferred fields of ONE captured Provisionssatz.
     *
     * Always against the draft, in both modes: the Provisionssatz is brand new either way, so none
     * of the ten can carry a value yet and all ten correctly show the pending sentence.
     */
    getDeferredAgentFields(draft: CpProvisionContractAgentDraft): CpWizardFieldState[] {
        return this.state.describeDeferredAgentFields(draft?.value);
    }

    /** The five liability fields of ONE captured Provisionssatz the user states, same rule. */
    getCapturedAgentFields(draft: CpProvisionContractAgentDraft): CpWizardFieldState[] {
        return this.state.describeCapturedAgentFields(draft?.value);
    }

    // ---------------------------------------------------------------------------------------- //
    // Save
    // ---------------------------------------------------------------------------------------- //

    canSave(): boolean {

        if (!this.canAgentChange || !this.state.isLastStep() || this.state.saveBusy) {
            return false;
        }

        if (!this.state.agentDrafts.length) {
            return false;
        }

        // Existing-parent mode captures no Provisionszeile; what it needs instead is the loaded row,
        // because the three denormalised keys of every child are copied off it.
        if (this.hasExistingContract()) {
            return !this.isExistingContractUnusable() && this.state.hasDerivableKeys();
        }

        return !!this.state.contractDraft;
    }

    /** Why the submit button is locked, in German — one reason per cause. */
    getSubmitTooltip(): string {

        if (!this.canAgentChange) {
            return CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
        }

        if (this.hasExistingContract() && this.isExistingContractUnusable()) {
            return 'Die Provisionszeile konnte nicht geladen werden.';
        }

        if (!this.hasExistingContract() && !this.state.contractDraft) {
            return 'Bitte zuerst die Provisionszeile erfassen.';
        }

        if (!this.state.agentDrafts.length) {
            return 'Bitte mindestens einen Provisionssatz erfassen.';
        }

        return this.hasExistingContract()
            ? 'Provisionssätze zur bestehenden Provisionszeile anlegen.'
            : 'Provisionszeile und Provisionssätze anlegen.';
    }

    handleSave(): void {

        if (!this.canSave()) {
            return;
        }

        this.runSaveSequence();
    }

    /**
     * Retry after a failed run.
     *
     * Deliberately the SAME path: every write checks its own resume point first, so the retry
     * continues exactly where it stopped and never repeats a write that succeeded.
     */
    handleRetrySave(): void {

        if (this.state.saveBusy) {
            return;
        }

        this.runSaveSequence();
    }

    /**
     * The write sequence: Provisionszeile first, then the Provisionssätze in capture order.
     *
     * The lock is raised by {@code beginSaveSequence} BEFORE anything leaves; a second start while a
     * sequence is in flight is refused there, not here.
     */
    protected runSaveSequence(): void {

        if (!this.state.beginSaveSequence()) {
            return;
        }

        this.subscription.add(
            this.state.ensureContractCreated(this.buildContractPayload()).pipe(
                switchMap((contract: ProvisionContractDto) => this.assertDerivableKeys(contract)),
                switchMap((contract: ProvisionContractDto) => this.state.ensureAgents(contract))
            ).subscribe({
                next: (agents: ProvisionContractAgentDto[]) => this.handleSaveSuccess(agents),
                error: (error: any) => this.handleSaveError(error)
            })
        );
    }

    /**
     * Between the two writes: does the parent carry the three keys every child is judged against?
     *
     * {@code ProvisionContractAgentOnChange.assertKeysDerivedFromProvisionContract} refuses a child
     * whose {@code contract}, {@code provisionType} and {@code provisionTypeContractEvent} disagree
     * with its parent, and it reads a MISSING value as a disagreement. All three are copied off the
     * parent in {@code buildAgentPayload}, so a parent without them would make every child write of
     * this run fail with a message about a rule the user never broke — which is exactly the wall the
     * old three-field dialog ran into.
     *
     * <p>Deliberately placed here rather than in either mode's setup, so it holds for BOTH: the row
     * this run just created and the row that was handed in. The check is on the wizard's OWN data —
     * "did the response/read give me what I have to copy" — and never on whether the server would
     * accept the child.
     */
    protected assertDerivableKeys(contract: ProvisionContractDto): Observable<ProvisionContractDto> {

        if (this.state.hasDerivableKeys()) {
            return of(contract);
        }

        this.state.saveStep = 'derive';

        return throwError(() => new Error(
            'Provision contract ' + contract?.id + ' carries no contract / provision type / event key.'));
    }

    /**
     * The create payload of the Provisionszeile — exactly what the metadata form captured.
     *
     * Nothing is added: every field the wizard decides sits on the CHILD, and the excluded
     * engine-owned fields have no control and therefore no key.
     */
    protected buildContractPayload(): DtoDetail {
        return this.state.copyValue(this.state.contractDraft);
    }

    protected handleSaveSuccess(agents: ProvisionContractAgentDto[]): void {

        this.state.endSaveSequence();
        this.state.saveError = null;
        this.state.saveHint = null;

        this.completed = true;

        if (this.hasExistingContract()) {
            this.messageService.showSuccessMessage(
                'Provisionssätze angelegt',
                agents.length + ' Provisionssätze wurden zur bestehenden Provisionszeile angelegt.');
        } else {
            this.messageService.showSuccessMessage(
                'Provisionszeile angelegt',
                'Die Provisionszeile und ' + agents.length + ' Provisionssätze wurden angelegt.');
        }

        this.informObjectChanged();
        this.onContractCreated.emit(this.state.createdContract);
    }

    /**
     * One write of the sequence failed.
     *
     * Everything written before it stays and is reused by the retry. The message is the server's own
     * refusal — the guards of this module answer in German and already name the rule.
     */
    protected handleSaveError(error: any): void {

        this.state.endSaveSequence();
        this.state.resolveErrorMessage(error);

        this.demoteOnForbidden(error);

        if (CpFeatureRight.isForbidden(error)) {
            this.state.saveError = CpFeatureRight.deniedMessage(CpFeatureRight.AGENT_CHANGE);
            this.state.saveHint = null;
        }

        this.messageService.showErrorMessage('Anlage fehlgeschlagen', this.state.saveError);
    }

    /**
     * ONLY a 403 demotes: a 500 or a dropped connection must leave the flag alone, otherwise a
     * transient backend fault would silently take the action away from a user who holds the right.
     */
    protected demoteOnForbidden(error: any): void {

        if (CpFeatureRight.isForbidden(error)) {
            this.canAgentChange = false;
            this.onForbidden.emit();
        }
    }

    /**
     * Announce what this run ACTUALLY wrote — one event per kind of row that came into existence.
     *
     * <h2>The bug this replaced</h2>
     * The previous version announced ONE alias chosen by MODE: {@code cp.ProvisionContract} for a new
     * parent, {@code cp.ProvisionContractAgent} for an existing one. In the new-parent case that was
     * wrong twice over — it named a type nothing listens for, and it stayed silent about the
     * Provisionssätze the run had just created. The worklist this wizard is opened from subscribes to
     * {@code cp.ProvisionContractAgent} alone
     * ({@code cp-provision-agent-contract-agents.component.ts:292}), so the event fired and reached
     * nobody. The row existed, the list did not know, and the TSDoc claimed otherwise — which is why
     * nobody looked.
     *
     * <h2>What decides now: what happened, not which mode</h2>
     * The parent is announced only when THIS run created it — {@link CpProvisionContractWizardStateService#existingContract}
     * separates a row we made from one the caller handed in, and announcing someone else's row as new
     * would be a different untruth. The Provisionssatz alias is announced when at least one draft was
     * actually written. Both can be true, one can be true, and after a partial failure that is exactly
     * the point: the run created a parent and possibly some children, and each is reported on its own
     * evidence.
     *
     * <h2>Why not bind (onCancel) in the host instead</h2>
     * That was the other candidate. It fixes ONE host: every future caller would have to remember the
     * binding, and forgetting it would reproduce this defect silently. It also overloads an output
     * that means "the user abandoned the run" into a refresh trigger, so the host would reload on
     * every cancel including the ones that wrote nothing. Announcing the truth here fixes it for every
     * listener, present and future, and leaves each of them to filter as it sees fit.
     */
    protected informObjectChanged(): void {

        // A parent this run created — not one that was handed in.
        if (this.state.createdContract && !this.state.existingContract) {

            this.mvsObjectService.objectChangeEvent.next(
                ObjectChangeInformation.create(
                    CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT,
                    ObjectChangeInformationActionEnum.created,
                    null,
                    this.state.createdContract));
        }

        if (this.state.agentDrafts.some((draft: CpProvisionContractAgentDraft) => draft.created)) {

            this.mvsObjectService.objectChangeEvent.next(
                ObjectChangeInformation.create(
                    CpProvisionContractWizardStateService.ALIAS_PROVISION_CONTRACT_AGENT,
                    ObjectChangeInformationActionEnum.created,
                    null,
                    null));
        }
    }

    // ---------------------------------------------------------------------------------------- //
    // Dialog frame
    // ---------------------------------------------------------------------------------------- //

    /**
     * Abandon the run.
     *
     * Refused while a write is in flight — cancelling mid-sequence would leave the user without the
     * retry that is the only way to finish what was already written.
     *
     * <p>When a previous attempt already wrote something, leaving is NOT silent. The row stays (see
     * the class TSDoc for why deleting it would be worse), so the user is told that it stays and what
     * it means for the next attempt — a Provisionszeile that quietly occupies its time slice would
     * make the NEXT run fail with a refusal the user themselves caused without knowing it.
     *
     * <p>The change event is raised for the same reason: without it the created row exists but no
     * surrounding list knows, so it is invisible exactly where the user would look for it. It is
     * deliberately NOT {@link onContractCreated} — that output means "the run succeeded", and this
     * run did not.
     */
    handleCancel(): void {

        if (this.state.saveBusy) {
            return;
        }

        if (this.state.hasPartialResult()) {

            this.messageService.showWarningMessage(
                'Provisionszeile bleibt bestehen',
                'Die Provisionszeile wurde bereits angelegt und wird durch den Abbruch nicht entfernt. '
                + 'Sie belegt damit ihren Zeitraum: ein neuer Anlauf mit demselben Vertrag, derselben '
                + 'Provisionsart und demselben Ereignisdatum wird abgewiesen. Ergänzen Sie die fehlenden '
                + 'Provisionssätze über die manuelle Anpassung.');

            this.informObjectChanged();
        }

        this.onCancel.emit();
        this.handleClose();
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {

        if (this.state.saveBusy) {
            return;
        }

        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {

        if (this.state.saveBusy && !value) {
            return;
        }

        this.visible = value;
        this.visibleChange.emit(value);
    }

}
